import { useEffect, useState } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Helmet } from "react-helmet-async";
import { getJobsDetailsUrl } from "Constant/api";
import { unAuthService } from "Service/Axios";
import { LoadingComponent } from "Components";
import {
  convertStringToHTML,
  convertStripHTML,
  formatDateWithFullMonth,
} from "Utils";
import { paths } from "Constant/paths";
import { BRAND_NAME } from "Constant/constants";

export const CareerDetails = () => {
  const { gid } = useParams();
  const navigate = useNavigate();
  const { loading: jobListLoading } = useOutletContext();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [jobsDetails, setJobsDetails] = useState(null);

  const baseUrl = process.env.REACT_APP_DEV_URL;
  const pageName = t("common.seo.careerJobDetails.pageName", {
    brandName: BRAND_NAME,
  });

  const jobTitle = jobsDetails?.name || "";
  const companyName = jobsDetails?.companyDetails?.name || "";

  const pageTitle = jobsDetails
    ? `${jobTitle} at ${companyName || BRAND_NAME} | ${BRAND_NAME}`
    : pageName;

  const metaDescription = jobsDetails?.details
    ? convertStripHTML(jobsDetails.details).substring(0, 160)
    : t("common.seo.careerJobDetails.metaDescription", {
        brandName: BRAND_NAME,
      });
  const canonicalUrl = `${baseUrl}/careers/jobs/${gid}`;

  const imageUrlRaw =
    jobsDetails?.image || "/assets/images/placeholders/company-placeholder.svg";
  const imageUrl = imageUrlRaw.startsWith("http")
    ? imageUrlRaw
    : `${baseUrl}${imageUrlRaw}`;

  const jobPostingJsonLd =
    jobsDetails && jobsDetails.jobOverview
      ? {
          "@context": "https://schema.org",
          "@type": "JobPosting",
          title: jobTitle,
          description: convertStripHTML(jobsDetails.details || ""),
          datePosted: jobsDetails.jobOverview.jobPosted || undefined,
          validThrough: jobsDetails.jobOverview.jobExpire || undefined,
          employmentType: jobsDetails.jobOverview.jobType || undefined,
          hiringOrganization: {
            "@type": "Organization",
            name: companyName || BRAND_NAME,
            sameAs: jobsDetails.companyDetails?.website || baseUrl,
            logo: imageUrl,
          },
          jobLocation: jobsDetails.jobOverview.location
            ? {
                "@type": "Place",
                address: {
                  "@type": "PostalAddress",
                  addressLocality: jobsDetails.jobOverview.location,
                },
              }
            : undefined,
          identifier: {
            "@type": "PropertyValue",
            name: companyName || BRAND_NAME,
            value: gid,
          },
          industry: jobsDetails.jobOverview.industry || undefined,
          baseSalary: jobsDetails.jobOverview.salary
            ? {
                "@type": "MonetaryAmount",
                value: {
                  "@type": "QuantitativeValue",
                  value: jobsDetails.jobOverview.salary,
                },
              }
            : undefined,
          url: canonicalUrl,
        }
      : null;

  const fetchJobsDetails = async (jobId) => {
    setLoading(true);
    const payload = {
      gid: jobId,
    };
    const response = await unAuthService(getJobsDetailsUrl, "POST", payload);
    if (!response) {
      setJobsDetails(null);
      setLoading(false);
      return;
    }
    setJobsDetails(response);
    setLoading(false);
  };

  useEffect(() => {
    fetchJobsDetails(gid);
  }, [gid]);

  const onRedirectToCareers = () => {
    navigate(paths.careers);
  };

  const onRedirectToApply = (formId, urlParameter, website, email) => {
    if (formId) {
      const formUrl = `${paths.formBuilder.applicationForm}/${formId}`;
      localStorage.setItem(
        "isFormAplication",
        urlParameter ? `${formUrl}?${urlParameter}` : formUrl
      );
      navigate(paths.registration);
    } else if (website) {
      window.open(website, "_blank", "noopener,noreferrer");
    } else if (email) {
      window.location.href = `mailto:${email}`;
    } else {
      toast.error(t("common.messages.notification.failed.somethingWentWrong"));
    }
  };

  return (
    <LoadingComponent isLoading={loading || jobListLoading}>
      <Helmet>
        <title>{pageTitle}</title>

        <meta name="description" content={metaDescription} />

        {/* Canonical */}
        <link rel="canonical" href={canonicalUrl} />

        {/* OpenGraph SEO */}
        <meta property="og:title" content={jobTitle || pageName} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:image" content={imageUrl} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="article" />

        {/* Twitter SEO */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content={
            jobTitle ? `${jobTitle} | ${companyName || BRAND_NAME}` : pageName
          }
        />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={imageUrl} />

        {/* JSON-LD JobPosting */}
        {jobPostingJsonLd && (
          <script type="application/ld+json">
            {JSON.stringify(jobPostingJsonLd)}
          </script>
        )}
      </Helmet>

      <section className="pb-10 border-bottom border-grey-200">
        {jobsDetails ? (
          <div className="container-xl">
            <div className="row align-items-center row-gap-4 mb-5">
              <div className="col-lg-7 col-12">
                <div className="d-flex flex-wrap align-items-center gap-3">
                  <button
                    className="size-10 p-1 d-flex justify-content-center align-items-center border border-grey-200 bg-gray-50 rounded-circle"
                    onClick={onRedirectToCareers}
                  >
                    <i className="bx bx-arrow-back"></i>
                  </button>
                  <div className="size-20 rounded-circle overflow-hidden">
                    <img
                      src={imageUrlRaw}
                      alt={jobsDetails.companyDetails?.name || jobsDetails.name}
                      className="w-100 h-100 object-fit-cover"
                    />
                  </div>
                  <div className="d-flex flex-column justify-content-start row-gap-2">
                    <div className="d-flex flex-wrap gap-2">
                      <h1 className="mb-0 text-lg">{jobsDetails.name}</h1>
                      <div className="d-flex gap-1">
                        <ul className="list-unstyled mb-0 d-inline-flex flex-wrap gap-1">
                          {jobsDetails.featured && (
                            <li>
                              <span className="px-2 py-1 rounded-pill text-xs text-red-500 bg-red-100">
                                Featured
                              </span>
                            </li>
                          )}
                          {jobsDetails.jobOverview?.jobType && (
                            <li>
                              <span className="px-2 py-1 rounded-pill text-xs text-primary-500 bg-primary-100">
                                {jobsDetails.jobOverview.jobType}
                              </span>
                            </li>
                          )}
                        </ul>
                      </div>
                    </div>
                    <ul className="list-unstyled mb-0 d-flex flex-wrap align-items-center gap-2 job-applicant-details">
                      {jobsDetails.companyDetails?.website && (
                        <li>
                          <a
                            href={jobsDetails.companyDetails.website}
                            className="d-flex align-items-center gap-1 text-decoration-none text-ingerit"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <i className="bx bx-link"></i>{" "}
                            <span>{jobsDetails.companyDetails.website}</span>
                          </a>
                        </li>
                      )}
                      {jobsDetails.companyDetails?.phone && (
                        <li>
                          <a
                            href={`tel:${jobsDetails.companyDetails.phone}`}
                            className="d-flex align-items-center gap-1 text-decoration-none text-ingerit"
                          >
                            <i className="bx bx-phone"></i>{" "}
                            <span>{jobsDetails.companyDetails.phone}</span>
                          </a>
                        </li>
                      )}
                      {jobsDetails.companyDetails?.email && (
                        <li>
                          <a
                            href={`mailto:${jobsDetails.companyDetails.email}`}
                            className="d-flex align-items-center gap-1 text-decoration-none text-ingerit"
                          >
                            <i className="bx bx-envelope"></i>{" "}
                            <span>{jobsDetails.companyDetails.email}</span>
                          </a>
                        </li>
                      )}
                    </ul>
                  </div>
                </div>
              </div>
              <div className="col-lg-5 col-12">
                <div className="d-flex flex-column align-items-lg-end row-gap-2">
                  <div className="d-flex gap-2">
                    <button
                      className="btn btn-primary px-8 font-semibold"
                      onClick={() =>
                        onRedirectToApply(
                          jobsDetails.formId,
                          jobsDetails.urlParameter,
                          jobsDetails.website,
                          jobsDetails.email
                        )
                      }
                    >
                      Apply Now <i className="bx bx-right-arrow-alt"></i>
                    </button>
                  </div>

                  {jobsDetails.jobOverview?.jobExpire && (
                    <div className="text-grey-500">
                      Job Expiry:{" "}
                      <span className="font-medium text-red-500">
                        {formatDateWithFullMonth(
                          jobsDetails.jobOverview.jobExpire
                        )}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="row align-items-start row-gap-4">
              <div className="col-lg-7 col-12">
                <div className="d-flex flex-column align-items-start row-gap-4">
                  {jobsDetails.details && (
                    <div className="d-flex flex-column row-gap-3 text-grey-500">
                      <h3 className="text-lg font-semibold text-grey-800">
                        Job Details
                      </h3>
                      <div
                        dangerouslySetInnerHTML={{
                          __html: convertStringToHTML(jobsDetails.details),
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
              <div className="col-lg-5 col-12">
                <div className="d-flex flex-column row-gap-4">
                  {jobsDetails.jobOverview && (
                    <div className="p-4 border border-grey-200 rounded-lg">
                      <h3 className="mb-5 text-lg font-semibold text-grey-800">
                        Job Overview
                      </h3>
                      <div className="row justify-content-start align-items-center gy-3">
                        {jobsDetails.jobOverview.jobPosted && (
                          <div className="col-xl-4 col-lg-4 col-md-4 col-sm-4 col-6">
                            <span className="text-decoration-none rounded-lg d-flex flex-column gap-2 text-inherit">
                              <div className="job-works-icon">
                                <i className="bx bx-calendar-alt bx-sm text-primary-800"></i>
                              </div>

                              <div>
                                <h5 className="mb-1 text-xs text-grey-400">
                                  Job Posted:
                                </h5>
                                <p className="mb-0 font-semibold text-grey-800">
                                  {formatDateWithFullMonth(
                                    jobsDetails.jobOverview.jobPosted
                                  )}
                                </p>
                              </div>
                            </span>
                          </div>
                        )}

                        {jobsDetails.jobOverview.jobExpire && (
                          <div className="col-xl-4 col-lg-4 col-md-4 col-sm-4 col-6">
                            <span className="text-decoration-none rounded-lg d-flex flex-column gap-2 text-inherit">
                              <div className="job-works-icon">
                                <i className="bx bx-stopwatch bx-sm text-primary-800"></i>
                              </div>

                              <div>
                                <h5 className="mb-1 text-xs text-grey-400">
                                  Job Expiry:
                                </h5>
                                <p className="mb-0 font-semibold text-grey-800">
                                  {formatDateWithFullMonth(
                                    jobsDetails.jobOverview.jobExpire
                                  )}
                                </p>
                              </div>
                            </span>
                          </div>
                        )}

                        {jobsDetails.jobOverview.education && (
                          <div className="col-xl-4 col-lg-4 col-md-4 col-sm-4 col-6">
                            <span className="text-decoration-none rounded-lg d-flex flex-column gap-2 text-inherit">
                              <div className="job-works-icon">
                                <i className="bx bx-briefcase bx-sm text-primary-800"></i>
                              </div>

                              <div>
                                <h5 className="mb-1 text-xs text-grey-400">
                                  Education
                                </h5>
                                <p className="mb-0 font-semibold text-grey-800">
                                  {jobsDetails.jobOverview.education}
                                </p>
                              </div>
                            </span>
                          </div>
                        )}

                        {jobsDetails.jobOverview.salary && (
                          <div className="col-xl-4 col-lg-4 col-md-4 col-sm-4 col-6">
                            <span className="text-decoration-none rounded-lg d-flex flex-column gap-2 text-inherit">
                              <div className="job-works-icon">
                                <i className="bx bx-wallet bx-sm text-primary-800"></i>
                              </div>

                              <div>
                                <h5 className="mb-1 text-xs text-grey-400">
                                  Salary:
                                </h5>
                                <p className="mb-0 font-semibold text-grey-800">
                                  {jobsDetails.jobOverview.salary}
                                </p>
                              </div>
                            </span>
                          </div>
                        )}

                        {jobsDetails.jobOverview.location && (
                          <div className="col-xl-4 col-lg-4 col-md-4 col-sm-4 col-6">
                            <span className="text-decoration-none rounded-lg d-flex flex-column gap-2 text-inherit">
                              <div className="job-works-icon">
                                <i className="bx bx-map bx-sm text-primary-800"></i>
                              </div>

                              <div>
                                <h5 className="mb-1 text-xs text-grey-400">
                                  Location:
                                </h5>
                                <p className="mb-0 font-semibold text-grey-800">
                                  {jobsDetails.jobOverview.location}
                                </p>
                              </div>
                            </span>
                          </div>
                        )}

                        {jobsDetails.jobOverview.jobType && (
                          <div className="col-xl-4 col-lg-4 col-md-4 col-sm-4 col-6">
                            <span className="text-decoration-none rounded-lg d-flex flex-column gap-2 text-inherit">
                              <div className="job-works-icon">
                                <i className="bx bx-briefcase bx-sm text-primary-800"></i>
                              </div>

                              <div>
                                <h5 className="mb-1 text-xs text-grey-400">
                                  Job Type:
                                </h5>
                                <p className="mb-0 font-semibold text-grey-800">
                                  {jobsDetails.jobOverview.jobType}
                                </p>
                              </div>
                            </span>
                          </div>
                        )}

                        {jobsDetails.jobOverview.experience && (
                          <div className="col-xl-4 col-lg-4 col-md-4 col-sm-4 col-6">
                            <span className="text-decoration-none rounded-lg d-flex flex-column gap-2 text-inherit">
                              <div className="job-works-icon">
                                <i className="bx bx-briefcase bx-sm text-primary-800"></i>
                              </div>

                              <div>
                                <h5 className="mb-1 text-xs text-grey-400">
                                  Experience
                                </h5>
                                <p className="mb-0 font-semibold text-grey-800">
                                  {jobsDetails.jobOverview.experience}
                                </p>
                              </div>
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  {jobsDetails.companyDetails && (
                    <div className="p-4 border border-grey-200 rounded-lg">
                      <div className="d-flex gap-3 align-items-center">
                        <div className="featured-job-icon flex-none w-14 h-14 rounded-lg d-flex justify-content-center align-items-center bg-primary-50 rounded-lg overflow-hidden">
                          <img
                            className="object-fit-cover w-100 h-100"
                            src={imageUrlRaw}
                            alt={jobsDetails.companyDetails.name}
                          />
                        </div>
                        <div>
                          <h4 className="mb-1 text-base font-semibold">
                            {jobsDetails.companyDetails.name}
                          </h4>
                          <p className="mb-0 text-sm text-grey-500 d-flex align-items-center gap-1">
                            {jobsDetails.companyDetails.description}
                          </p>
                        </div>
                      </div>

                      <div className="d-flex flex-column row-gap-2 my-5">
                        {jobsDetails.companyDetails.founded && (
                          <div className="row">
                            <div className="col-6 text-grey-500">
                              Founded In:
                            </div>
                            <div className="col-6">
                              {jobsDetails.companyDetails.founded}
                            </div>
                          </div>
                        )}
                        {jobsDetails.companyDetails.organizationType && (
                          <div className="row">
                            <div className="col-6 text-grey-500">
                              Organization Type:
                            </div>
                            <div className="col-6">
                              {jobsDetails.companyDetails.organizationType}
                            </div>
                          </div>
                        )}
                        {jobsDetails.companyDetails.companySize && (
                          <div className="row">
                            <div className="col-6 text-grey-500">
                              Company Size:
                            </div>
                            <div className="col-6">
                              {jobsDetails.companyDetails.companySize} Employees
                            </div>
                          </div>
                        )}
                        {jobsDetails.companyDetails.phone && (
                          <div className="row">
                            <div className="col-6 text-grey-500">Phone:</div>
                            <div className="col-6">
                              {jobsDetails.companyDetails.phone}
                            </div>
                          </div>
                        )}
                        {jobsDetails.companyDetails.email && (
                          <div className="row">
                            <div className="col-6 text-grey-500">Email:</div>
                            <div className="col-6">
                              <p className="mb-0 break-word">
                                {jobsDetails.companyDetails.email}
                              </p>
                            </div>
                          </div>
                        )}
                        {jobsDetails.companyDetails.website && (
                          <div className="row">
                            <div className="col-6 text-grey-500">Website:</div>
                            <div className="col-6">
                              <a
                                href={jobsDetails.companyDetails.website}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {jobsDetails.companyDetails.website}
                              </a>
                            </div>
                          </div>
                        )}
                      </div>

                      {jobsDetails.companyDetails.socialLinks && (
                        <ul className="social-icon-list list-unstyled d-inline-flex flex-wrap align-items-center gap-2">
                          {jobsDetails.companyDetails.socialLinks.facebook && (
                            <li>
                              <a
                                href={
                                  jobsDetails.companyDetails.socialLinks
                                    .facebook
                                }
                                target="_blank"
                                rel="noopener noreferrer"
                                className="size-8 rounded-md d-flex justify-content-center align-items-center"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="24"
                                  height="24"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    d="M13.397 20.997v-8.196h2.765l.411-3.209h-3.176V7.548c0-.926.258-1.56 1.587-1.56h1.684V3.127A22.336 22.336 0 0 0 14.201 3c-2.444 0-4.122 1.492-4.122 4.231v2.355H7.332v3.209h2.753v8.202h3.312z"
                                    fill="currentColor"
                                  ></path>
                                </svg>
                              </a>
                            </li>
                          )}
                          {jobsDetails.companyDetails.socialLinks.x && (
                            <li>
                              <a
                                href={jobsDetails.companyDetails.socialLinks.x}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="size-8 rounded-md d-flex justify-content-center align-items-center"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="24"
                                  height="24"
                                  viewBox="0 0 24 24"
                                  fill="currentColor"
                                >
                                  <path d="M10.4883 14.651L15.25 21H22.25L14.3917 10.5223L20.9308 3H18.2808L13.1643 8.88578L8.75 3H1.75L9.26086 13.0145L2.31915 21H4.96917L10.4883 14.651ZM16.25 19L5.75 5H7.75L18.25 19H16.25Z"></path>
                                </svg>
                              </a>
                            </li>
                          )}
                          {jobsDetails.companyDetails.socialLinks.instagram && (
                            <li>
                              <a
                                href={
                                  jobsDetails.companyDetails.socialLinks
                                    .instagram
                                }
                                target="_blank"
                                rel="noopener noreferrer"
                                className="size-8 rounded-md d-flex justify-content-center align-items-center"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="24"
                                  height="24"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    d="M11.999 7.377a4.623 4.623 0 1 0 0 9.248 4.623 4.623 0 0 0 0-9.248zm0 7.627a3.004 3.004 0 1 1 0-6.008 3.004 3.004 0 0 1 0 6.008z"
                                    fill="currentColor"
                                  ></path>
                                  <circle
                                    cx="16.806"
                                    cy="7.207"
                                    r="1.078"
                                    fill="currentColor"
                                  ></circle>
                                  <path
                                    d="M20.533 6.111A4.605 4.605 0 0 0 17.9 3.479a6.606 6.606 0 0 0-2.186-.42c-.963-.042-1.268-.054-3.71-.054s-2.755 0-3.71.054a6.554 6.554 0 0 0-2.184.42 4.6 4.6 0 0 0-2.633 2.632 6.585 6.585 0 0 0-.419 2.186c-.043.962-.056 1.267-.056 3.71 0 2.442 0 2.753.056 3.71.015.748.156 1.486.419 2.187a4.61 4.61 0 0 0 2.634 2.632 6.584 6.584 0 0 0 2.185.45c.963.042 1.268.055 3.71.055s2.755 0 3.71-.055a6.615 6.615 0 0 0 2.186-.419 4.613 4.613 0 0 0 2.633-2.633c.263-.7.404-1.438.419-2.186.043-.962.056-1.267.056-3.71s0-2.753-.056-3.71a6.581 6.581 0 0 0-.421-2.217zm-1.218 9.532a5.043 5.043 0 0 1-.311 1.688 2.987 2.987 0 0 1-1.712 1.711 4.985 4.985 0 0 1-1.67.311c-.95.044-1.218.055-3.654.055-2.438 0-2.687 0-3.655-.055a4.96 4.96 0 0 1-1.669-.311 2.985 2.985 0 0 1-1.719-1.711 5.08 5.08 0 0 1-.311-1.669c-.043-.95-.053-1.218-.053-3.654 0-2.437 0-2.686.053-3.655a5.038 5.038 0 0 1 .311-1.687c.305-.789.93-1.41 1.719-1.712a5.01 5.01 0 0 1 1.669-.311c.951-.043 1.218-.055 3.655-.055s2.687 0 3.654.055a4.96 4.96 0 0 1 1.67.311 2.991 2.991 0 0 1 1.712 1.712 5.08 5.08 0 0 1 .311 1.669c.043.951.054 1.218.054 3.655 0 2.436 0 2.698-.043 3.654h-.011z"
                                    fill="currentColor"
                                  ></path>
                                </svg>
                              </a>
                            </li>
                          )}
                          {jobsDetails.companyDetails.socialLinks.linkedin && (
                            <li>
                              <a
                                href={
                                  jobsDetails.companyDetails.socialLinks
                                    .linkedin
                                }
                                target="_blank"
                                rel="noopener noreferrer"
                                className="size-8 rounded-md d-flex justify-content-center align-items-center"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="24"
                                  height="24"
                                  viewBox="0 0 24 24"
                                >
                                  <circle
                                    cx="4.983"
                                    cy="5.009"
                                    r="2.188"
                                    fill="currentColor"
                                  ></circle>
                                  <path
                                    d="M9.237 8.855v12.139h3.769v-6.003c0-1.584.298-3.118 2.262-3.118 1.937 0 1.961 1.811 1.961 3.218v5.904H21v-6.657c0-3.27-.704-5.783-4.526-5.783-1.835 0-3.065 1.007-3.568 1.96h-.051v-1.66H9.237zm-6.142 0H6.87v12.139H3.095z"
                                    fill="currentColor"
                                  ></path>
                                </svg>
                              </a>
                            </li>
                          )}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="container-xl text-center">
            <h3 className="text-lg font-semibold mb-3">Job not found</h3>
            <p className="text-grey-500">
              The job you are looking for does not exist or has been removed.
            </p>
          </div>
        )}
      </section>
    </LoadingComponent>
  );
};
